export enum DynamicFilterValues {
  COMPONENT_PARENT_NONE = '@no-parent@',
  COMPONENT_CURRENT = '@current-component@',
}

/**
 * SpecialFilterKeys enumerates the special types of attributes that must be
 * handled differently than field attribute filters. Represents attributes that
 * does not exist on the object being filtered.
 *
 * Use dash-case or camelCase for default attribute filter ids. They are safe
 * to use. Custom fields use snake_case, so there will be no conflict.
 */
export enum SpecialFilterKeys {
  /**
   * Represents a special attribute value that must look up each reference to see
   * if the filtered component contains a reference to the specified component.
   */
  COMPONENT_HAS_REFERENCE_TO = '@@f_chrt@@',
  /**
   * Represents a special attribute value that must look up each reference to see
   * if the filtered component contains a reference from the specified component.
   */
  COMPONENT_HAS_REFERENCE_FROM = '@@f_chrf@@',
  TAGS = 'ardoq-tags',
}

export enum CommonDefaultFilterKeys {
  ID = '_id',
  TYPE = 'type',
  CREATED = 'created',
  LAST_UPDATED = 'last-updated',
  CREATED_BY = 'created-by',
  LAST_MODIFIED_BY = 'last-modified-by',
  ROOT_WORKSPACE = 'rootWorkspace',
  DESCRIPTION = 'description',
  TAG = 'tag',
}

export enum _ReferenceDefaultFilterKeys {
  DISPLAY_TEXT = 'displayText',
  TARGET = 'target',
  SOURCE = 'source',
  TARGET_WORKSPACE = 'targetWorkspace',
}

/** @deprecated Use isDefaultReferenceAttribute instead */
export const ReferenceDefaultFilterKeys = {
  ...CommonDefaultFilterKeys,
  ..._ReferenceDefaultFilterKeys,
};

export enum _ComponentDefaultFilterKeys {
  VERSION = '_version',
  NAME = 'name',
  PARENT = 'parent',
  INCOMING_REF_COUNT = 'incomingRefCount',
  OUTGOING_REF_COUNT = 'outgoingRefCount',
  COMPONENT_KEY = 'component-key',
}

/** @deprecated Use isDefaultComponentAttribute instead */
export const ComponentDefaultFilterKeys = {
  ...CommonDefaultFilterKeys,
  ..._ComponentDefaultFilterKeys,
};

const ComponentDefaultFilterKeySet = new Set(
  Object.values(ComponentDefaultFilterKeys)
);

export const isDefaultComponentAttribute = (val: string): boolean => {
  return ComponentDefaultFilterKeySet.has(
    val as CommonDefaultFilterKeys | _ComponentDefaultFilterKeys
  );
};

const ReferenceDefaultFilterKeySet = new Set(
  Object.values(ReferenceDefaultFilterKeys)
);

export const isDefaultReferenceAttribute = (val: string): boolean => {
  return ReferenceDefaultFilterKeySet.has(
    val as CommonDefaultFilterKeys | _ReferenceDefaultFilterKeys
  );
};

const isIncomingRefCount = (
  key: unknown
): key is _ComponentDefaultFilterKeys.INCOMING_REF_COUNT => {
  return key === _ComponentDefaultFilterKeys.INCOMING_REF_COUNT;
};

const isOutgoingRefCount = (
  key: unknown
): key is _ComponentDefaultFilterKeys.OUTGOING_REF_COUNT => {
  return key === _ComponentDefaultFilterKeys.OUTGOING_REF_COUNT;
};

const isTypeKey = (key: unknown): key is CommonDefaultFilterKeys.TYPE => {
  return key === CommonDefaultFilterKeys.TYPE;
};

export const filterKeyOperations = {
  isIncomingRefCount,
  isOutgoingRefCount,
  isTypeKey,
};
